"use client";
import { useMemo, useState } from "react";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";

const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-gray-800 bg-gray-900 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-200">
    {children}
  </span>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-gray-900 px-3 py-1 text-xs font-mono text-gray-200 border border-gray-800">
    {children}
  </span>
);

const LinkButton = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-800 bg-gray-950 px-4 py-2 text-sm font-semibold text-gray-100 hover:border-[#ff5500] hover:text-[#ff5500] transition duration-300"
  >
    {children}
    <ArrowUpRight size={15} />
  </a>
);

const formatRange = (start, end) => {
  if (!start && !end) return "—";
  const opts = { year: "numeric", month: "short" };
  const startStr = start
    ? new Date(start).toLocaleDateString("en-US", opts)
    : "N/A";
  const endStr = end
    ? new Date(end).toLocaleDateString("en-US", opts)
    : "Present";
  return `${startStr} → ${endStr}`;
};

const ensureArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const MediaCarousel = ({ items, posterFallback }) => {
  const [index, setIndex] = useState(0);
  if (!items.length) return null;

  const prev = () => setIndex((i) => (i - 1 + items.length) % items.length);
  const next = () => setIndex((i) => (i + 1) % items.length);

  const active = items[index];

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-800 bg-gray-950">
      {active.type === "image" ? (
        <img
          src={active.src}
          alt={active.alt}
          className="w-full h-auto object-cover"
        />
      ) : (
        <video
          src={active.src}
          className="w-full h-auto"
          autoPlay
          muted
          playsInline
          controls
          poster={active.poster || posterFallback || undefined}
        />
      )}

      {items.length > 1 && (
        <>
          <button
            onClick={prev}
            className="w-12 h-12 text-2xl absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 px-3 py-2 font-semibold text-white hover:bg-black/80 hover:cursor-pointer rounded-full"
          >
            ‹
          </button>
          <button
            onClick={next}
            className="w-12 h-12 text-2xl absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 px-3 py-2  font-semibold text-white hover:bg-black/80 hover:cursor-pointer"
          >
            ›
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
            {items.map((_, i) => (
              <span
                key={i}
                className={`h-2 w-2 rounded-full ${i === index ? "bg-cyan-400" : "bg-gray-600"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default function ProjectView({ project }) {
  const {
    name,
    summary,
    description,
    status,
    startDate,
    endDate,
    link,
    repo,
    screenshot,
    images,
    video,
    techStack,
    collaborators,
  } = project;

  const mediaItems = useMemo(() => {
    const gallery = ensureArray(images)
      .concat(ensureArray(screenshot))
      .filter(Boolean);
    const imagesList = gallery.map((src, idx) => ({
      type: "image",
      src,
      alt: `${name} screenshot ${idx + 1}`,
    }));
    const videos = ensureArray(video)
      .filter(Boolean)
      .map((src) => ({
        type: "video",
        src,
        poster: imagesList[0]?.src,
      }));
    return [...imagesList, ...videos];
  }, [images, screenshot, video, name]);

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* Header */}
        <header className="space-y-6 border-b border-gray-800 pb-10">
          <Link
            href="/#projects"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition group"
          >
            ← Back to projects
          </Link>

          <div className="space-y-4">
            <h1 className="text-4xl md:text-6xl font-black tracking-tight">
              {name}
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl leading-relaxed">
              {summary}
            </p>
          </div>

          <div className="flex gap-4 pt-2">
            {link && <LinkButton href={link}>Visit Site</LinkButton>}
            {repo && <LinkButton href={repo}>GitHub Repo</LinkButton>}
          </div>
        </header>

        <section className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-10">
            {mediaItems.length > 0 && (
              <MediaCarousel
                items={mediaItems}
                posterFallback={mediaItems[0]?.src}
              />
            )}

            <div className="prose prose-invert max-w-none">
              <h2 className="text-2xl font-bold mb-4">About the Project</h2>
              <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-wrap">
                {description}
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-8 h-fit lg:sticky lg:top-24">
            {/* Meta Card */}
            <div className="p-6 rounded-2xl border border-gray-800 bg-white/5 space-y-6">
              <div>
                <p className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-2">
                  Status & Timeline
                </p>
                <div className="flex flex-wrap gap-2 items-center">
                  <Badge>{status}</Badge>
                  <span className="text-sm text-gray-400">
                    {formatRange(startDate, endDate)}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-3">
                  Tech Stack
                </p>
                <div className="flex flex-wrap gap-2">
                  {techStack?.map((tech) => (
                    <Pill key={tech}>{tech}</Pill>
                  ))}
                </div>
              </div>

              {/* Project Links */}
              {(link || repo) && (
                <div className="pt-4 border-t border-gray-800 space-y-3">
                  <p className="text-xs font-mono text-gray-500 uppercase tracking-widest">
                    Project Links
                  </p>
                  <div className="flex flex-col gap-2.5">
                    {link && (
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between px-4 py-2.5 rounded-xl border border-gray-800 bg-black hover:border-[#ff5500]/50 hover:text-[#ff5500] text-sm font-semibold text-gray-200 transition duration-300 group"
                      >
                        <span>Visit Website</span>
                        <ArrowUpRight size={16} className="text-gray-500 group-hover:text-[#ff5500] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                      </a>
                    )}
                    {repo && (
                      <a
                        href={repo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between px-4 py-2.5 rounded-xl border border-gray-800 bg-black hover:border-[#ff5500]/50 hover:text-[#ff5500] text-sm font-semibold text-gray-200 transition duration-300 group"
                      >
                        <span>GitHub Repository</span>
                        <ArrowUpRight size={16} className="text-gray-500 group-hover:text-[#ff5500] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Collaborators */}
            {collaborators && collaborators.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">
                  Team
                </h3>
                <div className="space-y-3">
                  {collaborators.map((person) => (
                    <div
                      key={`${person.name}-${person.role}`}
                      className="flex items-center justify-between p-3 rounded-xl border border-gray-800 bg-black hover:border-gray-700 transition"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={person.avatar || "/image.png"}
                          alt={person.name}
                          className="h-10 w-10 rounded-full object-cover border border-gray-800"
                        />
                        <div>
                          <p className="font-medium text-sm text-white">
                            {person.name}
                          </p>
                          <p className="text-xs text-gray-500">{person.role}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {person.linkedin && (
                          <a
                            href={person.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-white transition"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                            </svg>
                          </a>
                        )}
                        {person.github && (
                          <a
                            href={person.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-white transition"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </section>
      </div>
    </div>
  );
}
